import { useState, useEffect, useRef, useCallback } from "react";
import { authHeaders } from "../supabase";

// ── Shared palette (matches EnrollmentAdvisor / Provider Intelligence) ──────
const C = {
  page:"#f8fafc", white:"#fff", blue:"#2563eb", blueDim:"#eff6ff", blueBdr:"#bfdbfe",
  blueD:"#1d4ed8", green:"#16a34a", greenDim:"#f0fdf4", greenBdr:"#bbf7d0",
  amber:"#d97706", amberDim:"#fffbeb", amberBdr:"#fde68a",
  text:"#111827", text2:"#374151", muted:"#6b7280", light:"#9ca3af",
  border:"#e5e7eb", bg:"#f9fafb",
};

const API_BASE = process.env.REACT_APP_API_URL || "https://api.mioprice.com";
const DEMO_USER = "jularyy@gmail.com";

const money = (n) => "$"+Math.round(n).toLocaleString();
// Range-aware: "—" when no contract, "$X" when single rate, "$X–$Y" when a range
const fmtRange = (r) => {
  if (!r || r.max==null) return "—";
  if (r.min==null || r.min===r.max) return money(r.max);
  return money(r.min) + "\u2013" + money(r.max);
};
// Plain-language billing class label (CPT codes never shown to users)
const bcLabel = (bc) => {
  const k = (bc||"").toLowerCase();
  if (k.startsWith("prof")) return "Physician fee";
  if (k.startsWith("inst")) return "Facility fee";
  return bc || "—";
};
const payerLabel = (p) => ({uhc:"UHC", aetna:"Aetna", cigna:"Cigna", kaiser:"Kaiser"}[p] || p.toUpperCase());

// Nominatim geocode for a "City, ST" string → {lat,lng}
async function geocodeCity(name) {
  try {
    const q = encodeURIComponent(name + ", USA");
    const r = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`);
    const j = await r.json();
    if (j && j[0]) return { lat: parseFloat(j[0].lat), lng: parseFloat(j[0].lon) };
  } catch (e) { /* ignore */ }
  return null;
}

export default function EnrollmentIntelligence() {
  // Cart
  const [cities, setCities]       = useState([]);   // [{name, lat, lng}]
  const [procedures, setProcs]    = useState([]);   // [{term, cpt}]
  const [state, setState]         = useState("IL");
  const [radius, setRadius]       = useState(15);
  const [topN, setTopN]           = useState(5);

  // City input + autocomplete
  const [cityInput, setCityInput] = useState("");
  const [cityList, setCityList]   = useState([]);   // all USPS cities
  const [citySugs, setCitySugs]   = useState([]);
  const [showCity, setShowCity]   = useState(false);
  const [geoBusy, setGeoBusy]     = useState(false);

  // Procedure input + autocomplete
  const [procInput, setProcInput] = useState("");
  const [termList, setTermList]   = useState([]);   // [{term, cpt}]
  const [procSugs, setProcSugs]   = useState([]);
  const [showProc, setShowProc]   = useState(false);

  // Results
  const [matrix, setMatrix]   = useState(null);   // full response
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const cityBoxRef = useRef(null);
  const procBoxRef = useRef(null);

  // Load city + term lists on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/cities`).then(r=>r.json())
      .then(d => setCityList(d.cities||[])).catch(()=>{});
    fetch(`${API_BASE}/api/search-terms`).then(r=>r.json())
      .then(d => setTermList(d.terms||[])).catch(()=>{});
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const f = (e) => {
      if (cityBoxRef.current && !cityBoxRef.current.contains(e.target)) setShowCity(false);
      if (procBoxRef.current && !procBoxRef.current.contains(e.target)) setShowProc(false);
    };
    document.addEventListener("mousedown", f);
    return () => document.removeEventListener("mousedown", f);
  }, []);

  // City autocomplete
  const onCityChange = (v) => {
    setCityInput(v);
    if (v.length < 2) { setCitySugs([]); setShowCity(false); return; }
    const lv = v.toLowerCase();
    setCitySugs(cityList.filter(c => c.toLowerCase().includes(lv)).slice(0, 8));
    setShowCity(true);
  };

  const addCity = useCallback(async (name) => {
    setCityInput(""); setCitySugs([]); setShowCity(false);
    if (cities.some(c => c.name === name)) return;
    setGeoBusy(true);
    const coords = await geocodeCity(name);
    setGeoBusy(false);
    if (!coords) { setError(`Couldn't locate ${name}. Try another city.`); return; }
    // Detect CA by bounding box, keep state in sync with first city added
    const inferCA = coords.lat < 42 && coords.lng < -114;
    if (cities.length === 0) setState(inferCA ? "CA" : "IL");
    setCities(prev => [...prev, { name, ...coords }]);
    setError("");
  }, [cities]);

  // Procedure autocomplete
  const onProcChange = (v) => {
    setProcInput(v);
    if (v.length < 2) { setProcSugs([]); setShowProc(false); return; }
    const lv = v.toLowerCase();
    setProcSugs(termList.filter(t => t.term.toLowerCase().includes(lv)).slice(0, 8));
    setShowProc(true);
  };

  const addProc = (t) => {
    setProcInput(""); setProcSugs([]); setShowProc(false);
    if (procedures.some(p => p.cpt === t.cpt)) return;
    setProcs(prev => [...prev, { term: t.term, cpt: t.cpt }]);
  };

  const removeCity = (name) => setCities(prev => prev.filter(c => c.name !== name));
  const removeProc = (cpt) => setProcs(prev => prev.filter(p => p.cpt !== cpt));
  const clearCart  = () => { setCities([]); setProcs([]); setMatrix(null); setError(""); };

  // Compare → call endpoint
  const runCompare = async () => {
    if (cities.length === 0 || procedures.length === 0) return;
    setLoading(true); setError(""); setMatrix(null);
    try {
      const res = await fetch(`${API_BASE}/api/enrollment-intelligence`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeaders()) },
        body: JSON.stringify({
          state, radius, top_n: topN,
          cities: cities.map(c => ({ name: c.name, lat: c.lat, lng: c.lng })),
          cpts: procedures.map(p => p.cpt),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(()=>({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setMatrix(data);
    } catch (e) {
      setError(e.message || "Comparison failed.");
    } finally {
      setLoading(false);
    }
  };

  const canCompare = cities.length > 0 && procedures.length > 0 && !loading;

  // Group matrix rows by city → cpt for readable sections
  const grouped = {};
  if (matrix?.matrix) {
    for (const row of matrix.matrix) {
      const ck = row.city || "—";
      const pk = row.cpt_label || row.cpt;
      grouped[ck] = grouped[ck] || {};
      grouped[ck][pk] = grouped[ck][pk] || [];
      grouped[ck][pk].push(row);
    }
  }
  const payers = matrix?.payers || [];

  return (
    <div style={{ background:C.page, minHeight:"100vh", padding:"20px 16px" }}>
      <div style={{ maxWidth:1100, margin:"0 auto" }}>

        {/* Header */}
        <div style={{ marginBottom:18 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:22 }}>🏢</span>
            <h1 style={{ fontSize:22, fontWeight:800, margin:0, color:C.text }}>Employer Intelligence</h1>
          </div>
          <p style={{ color:C.muted, fontSize:14, margin:"6px 0 0" }}>
            Build a comparison: add the cities your employees live in and the procedures they use,
            then compare real negotiated rates across payers at the top providers in each market.
          </p>
        </div>

        {/* ── Cart builder ───────────────────────────────────────────────── */}
        <div style={{ background:C.white, borderRadius:12, border:`1.5px solid ${C.border}`, padding:"18px 20px", marginBottom:16 }}>

          {/* Cities */}
          <div style={{ marginBottom:16 }} ref={cityBoxRef}>
            <label style={{ fontSize:12, fontWeight:700, color:C.text2, letterSpacing:0.3 }}>CITIES</label>
            <div style={{ position:"relative", marginTop:6 }}>
              <input
                value={cityInput}
                onChange={e => onCityChange(e.target.value)}
                onFocus={() => cityInput.length>=2 && setShowCity(true)}
                placeholder="Add a city (e.g. Naperville, IL)…"
                style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", fontSize:14,
                  border:`1px solid ${C.border}`, borderRadius:8, outline:"none", fontFamily:"inherit" }}
              />
              {showCity && citySugs.length>0 && (
                <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:20, marginTop:4,
                  background:C.white, border:`1px solid ${C.border}`, borderRadius:8,
                  boxShadow:"0 4px 14px rgba(0,0,0,0.10)", maxHeight:240, overflowY:"auto" }}>
                  {citySugs.map(c => (
                    <div key={c} onClick={() => addCity(c)}
                      style={{ padding:"9px 12px", fontSize:13, cursor:"pointer", borderBottom:`1px solid ${C.bg}` }}
                      onMouseEnter={e=>e.currentTarget.style.background=C.blueDim}
                      onMouseLeave={e=>e.currentTarget.style.background=C.white}>
                      {c}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:10 }}>
              {geoBusy && <span style={{ fontSize:12, color:C.muted }}>Locating…</span>}
              {cities.map(c => (
                <span key={c.name} style={{ display:"inline-flex", alignItems:"center", gap:6,
                  background:C.blueDim, border:`1px solid ${C.blueBdr}`, color:C.blueD,
                  borderRadius:20, padding:"5px 10px 5px 12px", fontSize:13, fontWeight:600 }}>
                  {c.name}
                  <button onClick={() => removeCity(c.name)} aria-label={`Remove ${c.name}`}
                    style={{ background:"none", border:"none", color:C.blueD, cursor:"pointer",
                      fontSize:16, lineHeight:1, padding:0 }}>×</button>
                </span>
              ))}
              {cities.length===0 && <span style={{ fontSize:12, color:C.light }}>No cities yet</span>}
            </div>
          </div>

          {/* Procedures */}
          <div style={{ marginBottom:16 }} ref={procBoxRef}>
            <label style={{ fontSize:12, fontWeight:700, color:C.text2, letterSpacing:0.3 }}>PROCEDURES</label>
            <div style={{ position:"relative", marginTop:6 }}>
              <input
                value={procInput}
                onChange={e => onProcChange(e.target.value)}
                onFocus={() => procInput.length>=2 && setShowProc(true)}
                placeholder="Add a procedure (e.g. MRI knee, colonoscopy)…"
                style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", fontSize:14,
                  border:`1px solid ${C.border}`, borderRadius:8, outline:"none", fontFamily:"inherit" }}
              />
              {showProc && procSugs.length>0 && (
                <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:20, marginTop:4,
                  background:C.white, border:`1px solid ${C.border}`, borderRadius:8,
                  boxShadow:"0 4px 14px rgba(0,0,0,0.10)", maxHeight:240, overflowY:"auto" }}>
                  {procSugs.map(t => (
                    <div key={t.cpt} onClick={() => addProc(t)}
                      style={{ padding:"9px 12px", fontSize:13, cursor:"pointer", borderBottom:`1px solid ${C.bg}`,
                        display:"flex", justifyContent:"space-between", gap:8 }}
                      onMouseEnter={e=>e.currentTarget.style.background=C.blueDim}
                      onMouseLeave={e=>e.currentTarget.style.background=C.white}>
                      <span>{t.term}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:10 }}>
              {procedures.map(p => (
                <span key={p.cpt} style={{ display:"inline-flex", alignItems:"center", gap:6,
                  background:C.greenDim, border:`1px solid ${C.greenBdr}`, color:C.green,
                  borderRadius:20, padding:"5px 10px 5px 12px", fontSize:13, fontWeight:600 }}>
                  {p.term}
                  <button onClick={() => removeProc(p.cpt)} aria-label={`Remove ${p.term}`}
                    style={{ background:"none", border:"none", color:C.green, cursor:"pointer",
                      fontSize:16, lineHeight:1, padding:0 }}>×</button>
                </span>
              ))}
              {procedures.length===0 && <span style={{ fontSize:12, color:C.light }}>No procedures yet</span>}
            </div>
          </div>

          {/* Controls */}
          <div style={{ display:"flex", flexWrap:"wrap", gap:16, alignItems:"flex-end",
            paddingTop:14, borderTop:`1px solid ${C.border}` }}>
            <div>
              <div style={{ fontSize:11, color:C.light, marginBottom:3 }}>Search radius</div>
              <select value={radius} onChange={e=>setRadius(parseInt(e.target.value))}
                style={{ padding:"7px 10px", fontSize:13, border:`1px solid ${C.border}`,
                  borderRadius:7, fontFamily:"inherit", cursor:"pointer" }}>
                {[10,15,25,50].map(r => <option key={r} value={r}>{r} miles</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:11, color:C.light, marginBottom:3 }}>Providers per market</div>
              <select value={topN} onChange={e=>setTopN(parseInt(e.target.value))}
                style={{ padding:"7px 10px", fontSize:13, border:`1px solid ${C.border}`,
                  borderRadius:7, fontFamily:"inherit", cursor:"pointer" }}>
                {[3,5,10].map(n => <option key={n} value={n}>Top {n}</option>)}
              </select>
            </div>
            <div style={{ flex:1 }} />
            <button onClick={clearCart}
              style={{ padding:"10px 16px", fontSize:13, fontWeight:600, color:C.muted,
                background:C.white, border:`1.5px solid ${C.border}`, borderRadius:8,
                cursor:"pointer", fontFamily:"inherit" }}>
              Clear
            </button>
            <button onClick={runCompare} disabled={!canCompare}
              style={{ padding:"10px 22px", fontSize:14, fontWeight:700, color:C.white,
                background:canCompare?C.blue:C.blueBdr, border:"none", borderRadius:8,
                cursor:canCompare?"pointer":"not-allowed", fontFamily:"inherit" }}>
              {loading ? "Comparing…" : "Compare"}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ background:C.amberDim, border:`1px solid ${C.amberBdr}`, color:C.amber,
            borderRadius:10, padding:"12px 16px", fontSize:13, marginBottom:16 }}>
            {error}
          </div>
        )}

        {/* ── Results matrix ─────────────────────────────────────────────── */}
        {matrix && matrix.count===0 && !loading && (
          <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:12,
            padding:"32px 20px", textAlign:"center", color:C.muted, fontSize:14 }}>
            No negotiated rates found for these procedures in the selected markets.
            Try a wider radius or different procedures.
          </div>
        )}

        {matrix && matrix.count>0 && (
          <div className="ei-report">
            {/* Report header (shown in print) */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <div>
                <h2 style={{ fontSize:18, fontWeight:800, margin:0, color:C.text }}>Cross-payer rate comparison</h2>
                <p style={{ fontSize:12, color:C.muted, margin:"4px 0 0" }}>
                  {matrix.cities.length} cit{matrix.cities.length!==1?"ies":"y"} · {matrix.cpts.length} procedure{matrix.cpts.length!==1?"s":""} · top {matrix.top_n} providers per market · {matrix.radius}mi radius
                </p>
              </div>
              <button onClick={() => window.print()} className="ei-no-print"
                style={{ padding:"9px 16px", fontSize:13, fontWeight:600, color:C.blueD,
                  background:C.blueDim, border:`1px solid ${C.blueBdr}`, borderRadius:8,
                  cursor:"pointer", fontFamily:"inherit" }}>
                Generate report
              </button>
            </div>

            {Object.entries(grouped).map(([city, procs]) => (
              <div key={city} style={{ marginBottom:18 }}>
                <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:8,
                  display:"flex", alignItems:"center", gap:6 }}>
                  📍 {city}
                </div>
                {Object.entries(procs).map(([proc, rows]) => (
                  <div key={proc} style={{ background:C.white, border:`1.5px solid ${C.border}`,
                    borderRadius:12, overflow:"hidden", marginBottom:12 }}>
                    <div style={{ padding:"10px 16px", background:C.bg, borderBottom:`1px solid ${C.border}`,
                      fontSize:13, fontWeight:700, color:C.text2 }}>{proc}</div>
                    <div style={{ overflowX:"auto" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                        <thead>
                          <tr style={{ background:C.white }}>
                            <th style={th(C, "left")}>Provider</th>
                            <th style={th(C, "left")}>Fee type</th>
                            {payers.map(p => (
                              <th key={p} style={th(C, "right")}>{payerLabel(p)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, i) => {
                            // best = lowest max across payers in this row (matches ranking)
                            const maxes = payers.map(p=>row.rates[p])
                              .filter(v=>v && v.max!=null).map(v=>v.max);
                            const best = maxes.length ? Math.min(...maxes) : null;
                            return (
                              <tr key={row.npi+row.billing_class+i}
                                style={{ borderTop:`1px solid ${C.border}` }}>
                                <td style={td(C)}>
                                  <div style={{ fontWeight:600, color:C.text }}>{row.provider || "—"}</div>
                                  {row.address && <div style={{ fontSize:11, color:C.light }}>{row.address}</div>}
                                </td>
                                <td style={td(C)}>
                                  <span style={{ fontSize:11, fontWeight:600,
                                    color: bcLabel(row.billing_class)==="Facility fee" ? C.amber : C.blueD,
                                    background: bcLabel(row.billing_class)==="Facility fee" ? C.amberDim : C.blueDim,
                                    border:`1px solid ${bcLabel(row.billing_class)==="Facility fee" ? C.amberBdr : C.blueBdr}`,
                                    borderRadius:6, padding:"2px 8px" }}>
                                    {bcLabel(row.billing_class)}
                                  </span>
                                </td>
                                {payers.map(p => {
                                  const v = row.rates[p];
                                  const hasRate = v && v.max!=null;
                                  const isBest = hasRate && v.max===best && maxes.length>1;
                                  return (
                                    <td key={p} style={{ ...td(C), textAlign:"right",
                                      whiteSpace:"nowrap",
                                      fontWeight: isBest ? 800 : 600,
                                      color: !hasRate ? C.light : isBest ? C.green : C.text }}>
                                      {fmtRange(v)}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {/* Disclaimer — MRF rate validity */}
            <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:10,
              padding:"11px 14px", fontSize:11, color:C.light, lineHeight:1.7, marginTop:4 }}>
              <strong style={{ color:C.muted }}>ⓘ</strong> Rates shown are <strong style={{ color:C.muted }}>negotiated</strong> and <strong style={{ color:C.muted }}>fee schedule</strong> amounts only — real contracted rates published under the federal Transparency in Coverage rule. Derived, percentage, and per diem rate types are excluded. Shown per payer (plan-level detail not yet differentiated). Physician and facility fees are shown separately and are never combined. A dash (—) means no published contract for that payer at that provider. These rates are not valid for cash-pay, uninsured, Medicaid, or Medicare patients.
            </div>
          </div>
        )}
      </div>

      {/* Print styles for "Generate report" */}
      <style>{`
        @media print {
          .ei-no-print { display:none !important; }
          body { background:#fff !important; }
        }
      `}</style>
    </div>
  );
}

// Table cell style helpers
function th(C, align) {
  return { textAlign:align, padding:"9px 16px", fontSize:11, fontWeight:700,
    color:C.muted, letterSpacing:0.3, borderBottom:`1px solid ${C.border}`,
    whiteSpace:"nowrap", background:C.white };
}
function td(C) {
  return { padding:"10px 16px", verticalAlign:"top" };
}
